import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaCalendarAlt, FaMapMarkerAlt, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';

const chapelImage1 = "/work-images/kaple1.png";
const chapelImage2 = "/work-images/kaple2.png";
const houseImage1 = "/work-images/navrh-dum.png";
const bathroomImage1 = "/work-images/koupelna1.png";
const bathroomImage2 = "/work-images/koupelna2.png";


interface ProjectData {
  id: string;
  title: string;
  date: string;
  location: string;
  description: string;
  details: string;
  images: string[];
  category: string;
  status: string;
}

const WorkDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  const projects: ProjectData[] = [
    {
      id: "1",
      title: "Kaple",
      date: "2021",
      location: "Pozořice, Česká republika",
      description: "Návrh moderní kaple ze dřeva",
      details: "Tvar kaple vychází z křesťanského symbolu trojúhelníku a je tvořen třemi hmotami, které výškově gradují směrem k presbytáři. Prosklená vstupní fasáda se symbolem kříže a skleněné výplně mezi hmotami přivádějí do interiéru přirozené světlo. Součástí návrhu je také kompletní řešení interiéru kaple včetně interiérových a liturgických prvků. Kaple i všechny interiérové prvky jsou navrženy ze dřeva, které podporuje klidnou a přirozenou atmosféru prostoru.",
      images: [chapelImage2, chapelImage1],
      category: "Sakrální",
      status: "Dokončeno"
    },
    {
      id: "2",
      title: "Rekonstrukce domu",
      date: "2024",
      location: "Moravskoslezský kraj, Česká republika",
      description: "Návrh rekonstrukce střechy a oken rodinného domu.",
      details: "Vizualizace architektonických úprav rodinného domu se zaměřením na zkrácení přesahů střechy, nové řešení fasády a úpravu oken s cílem zklidnění a sjednocení celkového výrazu stavby.",
      images: [houseImage1],
      category: "Domácnost",
      status: "Dokončeno"
    },
    {
      id: "3",
      title: "Koupelna",
      date: "2025",
      location: "Třinec, Česká republika",
      description: "Rekonstrukce koupelny rodinneho domu",
      details: "Návrh moderní koupelny v Třinci vznikl podle přání klientky, která chtěla světlý, jednoduchý a nadčasový prostor. Koupelna je laděna do jemných tónů, díky nimž působí vzdušněji a opticky větší.",
      images: [bathroomImage1, bathroomImage2],
      category: "Koupelna",
      status: "Dokončeno"
    }
  ];

  const project = projects.find(p => p.id === id);

  // Keyboard navigation for modal - must be before any conditional returns
  useEffect(() => {
    if (!isModalOpen || !project) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsModalOpen(false);
      } else if (e.key === 'ArrowLeft') {
        setModalImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
      } else if (e.key === 'ArrowRight') {
        setModalImageIndex((prev) => (prev + 1) % project.images.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, project]);

  if (!project) {
    return (
      <div className="work-detail-page">
        <div className="work-detail-container">
          <h2>Projekt nenalezen</h2>
          <Link to="/" className="back-button">
            <FaArrowLeft /> Zpět na portfolio
          </Link>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
  };

  const openModal = (imageIndex: number) => {
    setModalImageIndex(imageIndex);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const nextModalImage = () => {
    setModalImageIndex((prev) => (prev + 1) % project.images.length);
  };

  const prevModalImage = () => {
    setModalImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
  };

  return (
    <div className="work-detail-page">
      <div className="work-detail-container">
        <Link to="/" className="back-button">
          <FaArrowLeft /> Zpět na hlavní stránku
        </Link>

        <div className="project-header">
          <h1 className="project-title">{project.title}</h1>
          <div className="project-meta">
            <div className="meta-item">
              <FaCalendarAlt />
              <span>{project.date}</span>
            </div>
            <div className="meta-item">
              <FaMapMarkerAlt />
              <span>{project.location}</span>
            </div>
            <div className="meta-item">
              <span className="category">{project.category}</span>
            </div>
            <div className="meta-item">
              <span className={`status ${project.status.toLowerCase().replace(' ', '-')}`}>
                {project.status}
              </span>
            </div>
          </div>
        </div>

        <div className="project-content">
          <div className="image-carousel">
            <div className="carousel-container">
              <img 
                src={project.images[currentImageIndex]} 
                alt={`${project.title} - obrázek ${currentImageIndex + 1}`}
                className="carousel-image"
                onClick={() => openModal(currentImageIndex)}
                style={{ cursor: 'pointer' }}
              />
              {project.images.length > 1 && (
                <>
                  <button className="carousel-btn prev" onClick={prevImage}>
                    <FaChevronLeft />
                  </button>
                  <button className="carousel-btn next" onClick={nextImage}>
                    <FaChevronRight />
                  </button>
                  <div className="carousel-indicators">
                    {project.images.map((_, index) => (
                      <button
                        key={`carousel-indicator-${index}`}
                        className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                        onClick={() => setCurrentImageIndex(index)}
                        aria-label={`Zobrazit obrázek ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="project-info">
            <div className="description-section">
              <h3>Popis projektu</h3>
              <p>{project.description}</p>
            </div>

            <div className="details-section">
              <h3>Detailní informace</h3>
              <p>{project.details}</p>
            </div>

            <div className="project-specs">
              <h3>Specifikace</h3>
              <div className="specs-grid">
                <div className="spec-item">
                  <strong>Kategorie:</strong>
                  <span>{project.category}</span>
                </div>
                <div className="spec-item">
                  <strong>Status:</strong>
                  <span>{project.status}</span>
                </div>
                <div className="spec-item">
                  <strong>Rok:</strong>
                  <span>{project.date}</span>
                </div>
                <div className="spec-item">
                  <strong>Lokace:</strong>
                  <span>{project.location}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {isModalOpen && (
        <div className="image-modal-overlay" onClick={closeModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal}>
              <FaTimes />
            </button>
            <div className="modal-image-container">
              <img 
                src={project.images[modalImageIndex]} 
                alt={`${project.title} - obrázek ${modalImageIndex + 1}`}
                className="modal-image"
              />
              {project.images.length > 1 && (
                <>
                  <button className="modal-nav-btn prev" onClick={prevModalImage}>
                    <FaChevronLeft />
                  </button>
                  <button className="modal-nav-btn next" onClick={nextModalImage}>
                    <FaChevronRight />
                  </button>
                  <div className="modal-indicators">
                    {project.images.map((_, index) => (
                      <button
                        key={`modal-indicator-${index}`}
                        className={`modal-indicator ${index === modalImageIndex ? 'active' : ''}`}
                        onClick={() => setModalImageIndex(index)}
                        aria-label={`Zobrazit obrázek ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkDetail;
